import { z } from 'zod';

import { DevicePlatform, UserAgentSchema } from '../staff/devices';

import { TerminalMeSchema } from './me';

// ---------------------------------------------------------------------------
// Constantes de transporte del realm terminal (contracts/terminal-api.md; research R-5)
// ---------------------------------------------------------------------------

/** Valor de `X-Requested-With` en toda mutación del realm terminal (anti-CSRF). */
export const TERMINAL_CSRF_HEADER_VALUE = 'fermin-marker';

/**
 * Token opaco de sesión (256 bits, 43 caracteres base64url) entregado una sola vez por
 * `POST /activate`; viaja en `Authorization: Bearer <token>` y en el `hello` del canal, nunca en
 * la URL. El esquema es laxo a propósito: un token con forma extraña debe responder
 * `401 UNAUTHENTICATED`, no `400 VALIDATION_ERROR`.
 */
export const TerminalSessionTokenSchema = z.string().min(1).max(512);
export type TerminalSessionToken = z.infer<typeof TerminalSessionTokenSchema>;

// ---------------------------------------------------------------------------
// Código de activación (research R-4; data-model.md · terminal_activation_codes)
// ---------------------------------------------------------------------------

/** Alfabeto sin caracteres ambiguos (sin 0, O, 1, I): 32 símbolos. Mismo que el código de traspaso de 002. */
export const TERMINAL_ACTIVATION_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const TERMINAL_ACTIVATION_CODE_LENGTH = 6;
/** Equivale a `^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$`. */
export const TERMINAL_ACTIVATION_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

const ACTIVATION_CODE_MESSAGE =
  'El código debe tener 6 caracteres (letras y dígitos, sin 0, O, 1 ni I)';

/**
 * Código tal como lo teclea el marcador: se normaliza (`trim` + mayúsculas) antes del patrón.
 * El servidor lo compara por `sha256Hex(code)` contra `code_hash`.
 */
export const TerminalActivationCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(TERMINAL_ACTIVATION_CODE_PATTERN, { message: ACTIVATION_CODE_MESSAGE });
export type TerminalActivationCode = z.infer<typeof TerminalActivationCodeSchema>;

// ---------------------------------------------------------------------------
// Datos del cliente (diagnóstico; data-model.md · terminal_sessions)
// ---------------------------------------------------------------------------

export const TERMINAL_BROWSER_MAX_LENGTH = 120;
/** P. ej. `Chrome 128` (lo calcula el marcador con `ua-parser-js`). */
export const TerminalBrowserSchema = z.string().max(TERMINAL_BROWSER_MAX_LENGTH);

/** `{ width, height, dpr }` de la pantalla (diagnóstico de tamaño de QR). */
export const TerminalScreenSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  dpr: z.number().positive(),
});
export type TerminalScreen = z.infer<typeof TerminalScreenSchema>;

// ---------------------------------------------------------------------------
// `POST /activate` (pública; rate limit `TERMINAL_ACTIVATION_RATE_LIMIT_PER_MIN`/min/IP)
// ---------------------------------------------------------------------------

/** `{ code, platform, browser?, user_agent, screen? }`. */
export const ActivateTerminalBodySchema = z.object({
  code: TerminalActivationCodeSchema,
  platform: DevicePlatform,
  browser: TerminalBrowserSchema.optional(),
  user_agent: UserAgentSchema,
  screen: TerminalScreenSchema.optional(),
});
export type ActivateTerminalBody = z.infer<typeof ActivateTerminalBodySchema>;

/**
 * `201 { session_token, me }`. **No** devuelve la clave de terminal: llega por el canal
 * (`terminal_key`, research R-10).
 */
export const ActivateTerminalResponseSchema = z.object({
  session_token: TerminalSessionTokenSchema,
  me: TerminalMeSchema,
});
export type ActivateTerminalResponse = z.infer<typeof ActivateTerminalResponseSchema>;

// ---------------------------------------------------------------------------
// `GET /health` (pública)
// ---------------------------------------------------------------------------

/** `200 { status: 'ok', realm: 'terminal' }`. */
export const TerminalHealthResponseSchema = z.object({
  status: z.literal('ok'),
  realm: z.literal('terminal'),
});
export type TerminalHealthResponse = z.infer<typeof TerminalHealthResponseSchema>;
